import type { AddMember } from '@trello/action-history';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Trace } from '../ActionMapTypes';
import type { CardActionFragment } from '../CardActionFragment.generated';
import { CardActionFragmentDoc } from '../CardActionFragment.generated';
import { CardActionDocument } from '../CardActionMutation.generated';
import { MemberNameFragmentDoc } from '../MemberNameFragment.generated';
import { NoopError } from '../NoopError';
import { onTracedCardUpdate } from '../onTracedCardUpdate';
import { recordCardAction } from '../recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

export const addMember = (
  card: LegacyCardModel,
  action: AddMember,
  trace: Trace,
) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const memberId = action.idMember;
  const { traceId } = trace;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment<CardActionFragment>({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment || cardFragment.idMembers.includes(memberId)) {
      throw new NoopError();
    }

    const newMembers = [...cardFragment.idMembers, memberId];

    recordCardAction(cardId, action);

    // don't await so we don't block the UI
    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          idMembers: newMembers,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'Card',
          id: card.id,
          subscribed: true,
          idMembers: newMembers,
        },
      },
    });
  } else {
    if (card.hasMember(action.idMember)) {
      throw new NoopError();
    }
    card.addMemberWithTracing(
      action.idMember,
      traceId,
      onTracedCardUpdate({ card, ...trace }),
    );
  }

  const memberFragment = client.readFragment({
    id: `Member:${memberId}`,
    fragment: MemberNameFragmentDoc,
  });
  return format('notification_added_member_to_card', {
    member: dangerouslyConvertPrivacyString(memberFragment.fullName),
  });
};
